import React from 'react';
import { usePerformanceManager } from '../hooks/usePerformanceManager';

/**
 * Site-wide Loading Progress Indicator Component
 * Provides consistent loading indicators across all pages
 */
const LoadingProgressIndicator = ({ 
  componentId,
  showProgress = true,
  showMessage = true,
  showDataSize = false,
  className = '',
  style = {}
}) => {
  const { loadingState, isLoading } = usePerformanceManager(componentId);

  if (!isLoading || !loadingState) {
    return null;
  }

  const progress = loadingState.progress || 0;
  const message = loadingState.message || 'Loading...';
  const dataSize = loadingState.dataSize || 0;
  const duration = loadingState.lastUpdate 
    ? loadingState.lastUpdate - loadingState.startTime 
    : performance.now() - loadingState.startTime;

  return (
    <div className={`loading-progress-indicator ${className}`} style={style}>
      <div className="loading-content">
        {showMessage && (
          <div className="loading-message">
            {message}
            {showDataSize && dataSize > 0 && (
              <span className="data-size"> ({dataSize} items)</span>
            )}
          </div>
        )}
        
        {showProgress && (
          <div className="progress-container">
            <div className="progress-bar">
              <div 
                className="progress-fill"
                style={{ width: `${progress}%` }}
              />
            </div>
            <div className="progress-text">
              {Math.round(progress)}%
            </div>
          </div>
        )}
        
        <div className="loading-spinner">
          <div className="spinner"></div>
        </div>
        
        {duration > 2000 && (
          <div className="loading-duration">
            {Math.round(duration / 1000)}s
          </div>
        )}
      </div>
      
      <style jsx>{`
        .loading-progress-indicator {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 20px;
          background: rgba(255, 255, 255, 0.95);
          border-radius: 8px;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
          min-height: 100px;
        }

        .loading-content {
          text-align: center;
          max-width: 300px;
        }

        .loading-message {
          font-size: 16px;
          color: #333;
          margin-bottom: 15px;
          font-weight: 500;
        }

        .data-size {
          font-size: 14px;
          color: #666;
          font-weight: normal;
        }

        .progress-container {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 15px;
          width: 100%;
        }

        .progress-bar {
          flex: 1;
          height: 8px;
          background: #e0e0e0;
          border-radius: 4px;
          overflow: hidden;
        }

        .progress-fill {
          height: 100%;
          background: linear-gradient(90deg, #4CAF50, #45a049);
          border-radius: 4px;
          transition: width 0.3s ease;
        }

        .progress-text {
          font-size: 14px;
          color: #666;
          min-width: 40px;
          text-align: right;
        }

        .loading-spinner {
          margin-bottom: 10px;
        }

        .spinner {
          width: 32px;
          height: 32px;
          border: 3px solid #e0e0e0;
          border-top: 3px solid #4CAF50;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        .loading-duration {
          font-size: 12px;
          color: #999;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        /* Mobile responsive */
        @media (max-width: 768px) {
          .loading-progress-indicator {
            padding: 15px;
            min-height: 80px;
          }

          .loading-message {
            font-size: 14px;
          }

          .spinner {
            width: 24px;
            height: 24px;
            border-width: 2px;
          }
        }
      `}</style>
    </div>
  );
};

/**
 * Simplified Loading Spinner for quick loading states
 */
export const LoadingSpinner = ({ size = 'medium', message = '', className = '' }) => {
  const sizeMap = {
    small: '16px',
    medium: '32px',
    large: '48px'
  };

  return (
    <div className={`loading-spinner-simple ${className}`}>
      {message && <div className="spinner-message">{message}</div>}
      <div 
        className="spinner"
        style={{ 
          width: sizeMap[size], 
          height: sizeMap[size],
          borderWidth: size === 'small' ? '2px' : '3px'
        }}
      />
      
      <style jsx>{`
        .loading-spinner-simple {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 10px;
        }

        .spinner-message {
          font-size: 14px;
          color: #666;
        }

        .spinner {
          border: 3px solid #e0e0e0;
          border-top: 3px solid #4CAF50;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

/**
 * Global Loading Overlay for full-page loading states
 */
export const GlobalLoadingOverlay = ({ show = false, message = 'Loading...', progress = null }) => {
  if (!show) return null;

  return (
    <div className="global-loading-overlay">
      <div className="overlay-content">
        <LoadingSpinner size="large" message={message} />
        {progress !== null && (
          <div className="overlay-progress">
            <div className="progress-bar">
              <div 
                className="progress-fill"
                style={{ width: `${progress}%` }}
              />
            </div>
            <div className="progress-text">{Math.round(progress)}%</div>
          </div>
        )}
      </div>
      
      <style jsx>{`
        .global-loading-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(255, 255, 255, 0.9);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 9999;
          backdrop-filter: blur(2px);
        }

        .overlay-content {
          text-align: center;
          padding: 30px;
          background: white;
          border-radius: 12px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
          min-width: 200px;
        }

        .overlay-progress {
          margin-top: 20px;
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .progress-bar {
          flex: 1;
          height: 8px;
          background: #e0e0e0;
          border-radius: 4px;
          overflow: hidden;
        }

        .progress-fill {
          height: 100%;
          background: linear-gradient(90deg, #4CAF50, #45a049);
          border-radius: 4px;
          transition: width 0.3s ease;
        }

        .progress-text {
          font-size: 14px;
          color: #666;
          min-width: 40px;
        }
      `}</style>
    </div>
  );
};

export default LoadingProgressIndicator;